"use client";

import { useChat } from "@ai-sdk/react";
import type { MagnusUIMessage as ChatMessage } from "@magnus/contract/messages";
import type { TurnOptions } from "@magnus/contract/turn";
import type { ChatTransport } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { abortActiveReader } from "../chat-client";
import { type ClientTtftRecord, createChatTransport, markPhase, messageMetadataSchema, TTFT_PHASE } from "../index";
import { serializeDeclaredTools } from "../tools/host-declaration";
import { buildTurnBody, resolveRuntimeTimeZone } from "./build-turn-body";
import { CoalescedChat } from "./coalesced-chat";
import { useMagnusClient } from "./provider";
import { hasCompletedInitialTurn, resumableRunId } from "./resume-state";
import { useLatest } from "./use-latest";
export interface UseMagnusOptions {
  /** Set when an in-flight stream should be resumed on mount. */
  activeStreamId?: string;
  chatId: string;
  /**
   * Per-send dynamic request body (model, workspace, capabilities, ...).
   * `appSurface` + `surfaceContext` + `surfacePromptAppendix` are merged in
   * automatically from the provider, and `timeZone` from the runtime's `Intl`,
   * so callers only supply their own fields. Typed via the
   * authored contract (`@magnus/contract/turn`) so illegal values (e.g. a
   * typo'd `modelId`) fail at compile time.
   */
  getBody?: () => TurnOptions;
  /** Server-rendered transcript to start from. Defaults to `[]` (new chat). */
  initialMessages?: ChatMessage[];
  /**
   * TTFT (time-to-first-token) observability hook. Fired once per send after
   * the first text delta paints, carrying the client-side phase marks + run
   * id. The host decides where these land (the web app beacons them to
   * `/api/chat/ttft-report`). Occasional-debugging telemetry; no-op when unset.
   */
  onClientTtft?: (record: ClientTtftRecord) => void;
  /** Fired when a turn finishes streaming. */
  onFinish?: () => void;
  /** Fired once when a brand-new chat starts streaming (e.g. to refresh a list). */
  onFirstStream?: () => void;
  /**
   * Persist partial messages when the user stops a turn. Wired by the host
   * to its backend (the web app calls `trpc.chat.stop`). The returned
   * promise is awaited before the next send so cancellation lands first.
   */
  onStop?: (partialMessages: ChatMessage[]) => unknown;
}
export interface UseMagnusResult {
  error: Error | undefined;
  messages: ChatMessage[];
  sendMessage: ReturnType<typeof useChat<ChatMessage>>["sendMessage"];
  setMessages: ReturnType<typeof useChat<ChatMessage>>["setMessages"];
  /** False only while replaying a buffered (resumed) stream we didn't initiate. */
  smoothTextEnabled: boolean;
  status: ReturnType<typeof useChat<ChatMessage>>["status"];
  stop: () => void;
}

/**
 * Default for `initialMessages`. Module-level so the identity is stable across
 * renders — it feeds `useChat`'s initial messages and several hook deps, and a
 * fresh `[]` each render would retrigger them. Shared by every hook instance
 * that omits `initialMessages`, so it must never be written to; frozen in dev
 * to make an accidental write fail loudly there rather than silently
 * cross-contaminating unrelated chats in production.
 */
const EMPTY_MESSAGES: ChatMessage[] = [];
if (process.env.NODE_ENV !== "production") {
  Object.freeze(EMPTY_MESSAGES);
}

/**
 * Drive a Magnus copilot conversation. Generalizes the web app's
 * `use-chat-agent` hook: the transport, base URL, and auth all come from
 * the `MagnusProvider` client, and host-specific persistence (stop,
 * list invalidation) is injected via callbacks instead of importing the
 * app's tRPC singleton.
 */
export function useMagnus(options: UseMagnusOptions): UseMagnusResult {
  const {
    chatId,
    initialMessages = EMPTY_MESSAGES,
    activeStreamId,
    getBody,
    onFinish,
    onFirstStream,
    onStop,
    onClientTtft
  } = options;
  const {
    client,
    surface,
    surfaceContext,
    surfacePromptAppendix,
    tools
  } = useMagnusClient();
  const onClientTtftRef = useLatest(onClientTtft);

  // Declarations for the provider's host-invented tools — the ones carrying
  // their own description + inputSchema (ADR-096).
  // Memoized on `tools` so a stable provider tool array yields a byte-stable
  // body — tool declarations sit in the provider's cacheable prompt prefix.
  const hostTools = useMemo(() => serializeDeclaredTools(tools), [tools]);

  // Merge the provider surface/context/prompt-appendix/tool declarations and
  // the runtime timezone into every request body (per-send `getBody` fields
  // win — see `buildTurnBody`). The zone is read at send time so a laptop
  // that crosses zones mid-chat stamps the next turn correctly.
  const buildBody = useCallback((): Record<string, unknown> => buildTurnBody({
    hostTools,
    surface,
    surfaceContext,
    surfacePromptAppendix,
    timeZone: resolveRuntimeTimeZone()
  }, getBody?.() ?? {}), [getBody, hostTools, surface, surfaceContext, surfacePromptAppendix]);
  const bodyRef = useLatest(buildBody);

  // Read activeStreamId through a ref so the transport memo stays stable for
  // the life of the chat (ADR-049 Rule 3).
  const activeStreamIdRef = useLatest(activeStreamId);

  // Smooth-typing gate: snap during resume replay, then stream live deltas.
  const sentLocallyRef = useRef(false);
  const [resumeReplayComplete, setResumeReplayComplete] = useState(false);
  const onReplayCompleteRef = useRef<() => void>(() => {
    setResumeReplayComplete(true);
  });

  // Run ids the user explicitly stopped. A stopped run is *not* resumable: its
  // durable belt keeps filling until the in-flight model step finishes
  // (`cancelRun()` only blocks future steps — see `ChatService.stop`'s
  // `TODO(stop)`), so a mount/resume would otherwise re-attach and drain the
  // rest of the cancelled turn into the store, making the reply keep
  // "streaming" after stop. `getRunId` consults this set so every
  // resume/reconnect for these runs short-circuits to `null`.
  const stoppedRunIdsRef = useRef<Set<string>>(new Set());

  // `activeStreamId` is a point-in-time snapshot from the server render: it
  // names whatever run was in flight when the page was rendered, and nothing on
  // the client ever refreshes or clears it. So it is good for exactly one
  // resume — the mount-time catch-up below. Left standing as a fallback it
  // makes every later reconnect re-attach to a long-finished run, and the
  // server replays that run's entire belt into the transcript, which reads as
  // the assistant looping forever on work it already did (feedback
  // TuCXnXvfrIg7SxmQQZiYx: a page rendered mid-run kept replaying that run on
  // every foreground for the next hour).
  //
  // Runs started in *this* session are tracked in session storage instead,
  // which is only cleared on a clean finish — so the dropped-connection case
  // the foreground re-attach exists for still resumes normally.
  const activeStreamIdSpentRef = useRef(false);

  // One catch-up resume per mount *and per chat* (used by the mount effect
  // below; declared here so the chat-switch reset can re-arm it). The one-shot
  // collapses React Strict Mode's double-invoke — the cleanup between the two
  // invocations must NOT reset it, or the second invocation fires a duplicate
  // resume whose registry takeover aborts the first mid-flight, and that
  // abort's ready-transition trips the empty-response error banner.
  const hasResumedRef = useRef(false);

  // Reset the gates when switching chats (parent stays mounted across ids).
  const lastChatIdRef = useRef(chatId);
  if (lastChatIdRef.current !== chatId) {
    lastChatIdRef.current = chatId;
    sentLocallyRef.current = false;
    stoppedRunIdsRef.current.clear();
    activeStreamIdSpentRef.current = false;
    // The switched-to chat gets its own catch-up resume; without this the
    // spent one-shot from the previous chat would silently skip it.
    hasResumedRef.current = false;
    setResumeReplayComplete(false);
  }

  // True from the moment the user presses stop until the resulting status
  // settles back to "ready". Distinguishes a user-initiated abort (a real end
  // of the turn, hosts should hear about it) from an internal one (unmount /
  // chat-switch teardown, reader-slot takeover) in the `onFinish` gate below.
  const userStoppedRef = useRef(false);

  // Hoisted out of the transport memo so the idempotent-resume helper below
  // can consult the same run id the transport will resume with.
  const getRunId = useCallback((): string | null => {
    const runKey = `workflow-run:${chatId}`;
    const fromSession = typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(runKey);
    // The server snapshot is single-use (see `activeStreamIdSpentRef`) but is
    // NOT consumed here — this getter runs twice per resume (dedupe helper +
    // transport), so consumption is marked where the run id is committed
    // (`setRunId`), retired on a clean finish (`clearRunId`), or retired once
    // the mount effect below has spent it on its one catch-up resume.
    const fromProp = activeStreamIdSpentRef.current ? undefined : activeStreamIdRef.current;
    const runId = resumableRunId(fromProp, fromSession);
    if (!runId) {
      return null;
    }
    // Never resume a run the user stopped — it's cancelled server-side and
    // resuming would replay the tail of the aborted turn (see
    // `stoppedRunIdsRef`). Prune the stale session marker while we're here.
    if (stoppedRunIdsRef.current.has(runId)) {
      if (fromSession && typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(runKey);
      }
      return null;
    }
    return runId;
  }, [chatId, activeStreamIdRef]);
  const transport = useMemo<ChatTransport<ChatMessage>>(() => {
    const runKey_0 = `workflow-run:${chatId}`;
    return createChatTransport({
      baseUrl: client.baseUrl,
      chatId,
      fetch: client.fetch,
      getBody: () => bodyRef.current(),
      getRunId,
      setRunId: runId_0 => {
        // The run id is committed to session storage from here on — the
        // server snapshot is spent (it can only describe an older run).
        activeStreamIdSpentRef.current = true;
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(runKey_0, runId_0);
        }
      },
      clearRunId: () => {
        // A clean finish also retires the server snapshot: a live send never
        // calls `getRunId`, so without this the prop would still be unspent and
        // the next foreground would resume the run that just ended.
        activeStreamIdSpentRef.current = true;
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(runKey_0);
        }
      },
      onReaderConflict: conflict => {
        // A second reader tried to attach to a chat that already had one. The
        // registry resolved it (suppressed the resume / aborted on takeover),
        // so this is informational — but it's the exact signature of the
        // jumbled-stream bug, so surface it for client telemetry.
        console.warn("[chat] concurrent stream reader resolved", conflict);
      },
      onResumeReplayComplete: () => onReplayCompleteRef.current?.(),
      onClientTtft: record => {
        // No host sink → skip the (cheap) paint mark entirely.
        if (!onClientTtftRef.current) {
          return;
        }
        // Add the first-paint mark on the next animation frame (a proxy for
        // when the first delta actually renders, after the coalesced notify),
        // then hand the completed record to the host. rAF is browser-only;
        // fall back to an immediate call on other hosts. Records flushed at
        // stream end (no text delta) get no paint mark — it would measure
        // the end of the turn, not a first paint.
        const flush = () => {
          if (record.phases[TTFT_PHASE.clientFirstTextDelta] !== undefined) {
            markPhase(record.phases, TTFT_PHASE.clientFirstPaint);
          }
          onClientTtftRef.current?.(record);
        };
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(flush);
        } else {
          flush();
        }
      }
    });
  }, [chatId, client, bodyRef, onClientTtftRef, getRunId]);

  // The transport and `onFinish` are read through refs so the `Chat` below
  // stays stable for the life of the chat id (mirrors what `useChat` does
  // internally when it owns the `Chat`).
  const transportRef = useLatest(transport);
  const onFinishRef = useLatest(onFinish);

  // We own the `Chat` instead of letting `useChat` create one so its React
  // notifications can be coalesced onto a macrotask (`CoalescedChat`). The
  // SDK's `experimental_throttle` is a wall-clock throttle that fires
  // synchronously from the stream's microtask chain whenever the previous
  // render outlasted the window — on heavy transcripts that drives React
  // into "Maximum update depth exceeded" (minified #185, the red banner) on
  // every turn. See `coalesced-chat.ts` for the mechanism.
  //
  // One `Chat` per chat id: `initialMessages` only seeds it (same as
  // `useChat`), so it is deliberately not a dependency.
  // biome-ignore lint/correctness/useExhaustiveDependencies: initialMessages seeds the store once per chat id; the rest are stable refs
  const chat = useMemo(() => new CoalescedChat<ChatMessage>({
    id: chatId,
    messages: initialMessages,
    messageMetadataSchema,
    transport: {
      sendMessages: sendOptions => transportRef.current.sendMessages(sendOptions),
      reconnectToStream: reconnectOptions => transportRef.current.reconnectToStream(reconnectOptions)
    },
    // The AI SDK fires `onFinish` on *every* request termination, including
    // aborted reads (`isAbort`). An internal abort — the unmount/chat-switch
    // teardown below, or a reader-slot takeover — only closes this client's
    // read cursor while the turn keeps running server-side, so surfacing it
    // as "the turn finished" is wrong (the web app would pop its "reply is
    // ready" notification for a reply that isn't). A user stop is the
    // exception: hosts treat it as the end of the turn (list invalidation,
    // bell reset), so it passes through.
    onFinish: ({
      isAbort
    }) => {
      if (isAbort && !userStoppedRef.current) {
        return;
      }
      onFinishRef.current?.();
    }
  }), [chatId, transportRef, onFinishRef]);

  // `useChat` only stops a `Chat` it created itself. Stopping ours on unmount
  // and on chat switch closes this client's read cursor (the durable run keeps
  // streaming server-side) and frees the chat's reader slot so the next
  // mount's resume attaches — see the note below the `useChat` call.
  useEffect(() => () => {
    chat.stop().catch(() => undefined);
  }, [chat]);
  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    error: sdkError,
    resumeStream
  } = useChat<ChatMessage>({
    chat
  });

  /**
   * Resume the active run, idempotently against a store that already holds
   * (part of) its assistant message.
   *
   * The resume transport replays the durable belt from index 0 — its chunk
   * cursor is per-attach, with no knowledge of what this store already
   * rendered. On a fresh mount that is exactly right (the in-flight assistant
   * message is not in `initialMessages`). But when the live send died before
   * its `finish` chunk (SSE severed by a dev-server restart or a Redis flap,
   * so `clearRunId` never fired) the store still holds the partial —or even
   * complete— assistant message, and the AI SDK *continues* the trailing
   * assistant message on resume (`createStreamingUIMessageState({ lastMessage })`
   * clones its parts). Feeding a full replay into that clone appends a second
   * copy of every part: assistant text rendered 2-3x with orphaned
   * "Thought for a few seconds" reasoning stubs between the copies (observed
   * live 2026-07-29, run wrun_01KYQP0BQW04PP7RY9BWGHVSE3: five resumes, all
   * `startIndex: 0`, against an already-complete turn).
   *
   * Fix: drop the trailing assistant message *iff it belongs to the run being
   * resumed* — the workflow's deterministic message ids are
   * `${runId}:a<iteration>:<retry>`, so ownership is a prefix check — and let
   * the replay rebuild it byte-identical from the belt. Repeat resumes
   * converge to exactly one copy instead of accumulating. Messages from other
   * runs are never touched, and with no resumable run this is a no-op.
   *
   * Must run *before* `resumeStream()`: the SDK snapshots `lastMessage` into
   * the streaming state as soon as the request starts, so trimming afterwards
   * cannot un-seed the clone. `setMessages` writes synchronously to the chat
   * store, so the ordering here is safe. Because the trim therefore has to
   * happen speculatively, a resume that never opens (offline, server
   * restarted) puts the message back — otherwise the user's completed turn
   * would visibly disappear until a reload.
   */
  const resumeStreamIdempotently = useCallback((): Promise<void> => {
    const runId_1 = getRunId();
    if (!runId_1) {
      // Nothing resumable — `resumeStream()` would no-op via the transport
      // returning null; skip the request entirely.
      return Promise.resolve();
    }
    let trimmed: ChatMessage | undefined;
    setMessages(prev => {
      const last = prev.at(-1);
      if (last?.role === "assistant" && last.id.startsWith(`${runId_1}:`)) {
        trimmed = last;
        return prev.slice(0, -1);
      }
      return prev;
    });
    return resumeStream().catch((error: unknown) => {
      const restore = trimmed;
      if (restore) {
        setMessages(prev_0 => {
          const last_0 = prev_0.at(-1);
          // A replay that opened and then broke has already rebuilt some of
          // this run's message; re-appending would duplicate it.
          return last_0?.role === "assistant" && last_0.id.startsWith(`${runId_1}:`) ? prev_0 : [...prev_0, restore];
        });
      }
      throw error;
    });
  }, [getRunId, setMessages, resumeStream]);

  // An active run always has the user's message last in the transcript. If the
  // server-rendered transcript already ends with an assistant message, the
  // turn's reply is persisted — there is nothing live to catch, so resuming
  // would only replay a completed run into the store (and risk a duplicate
  // render if `activeStreamId` is momentarily stale before cleanup clears it).
  const turnAlreadyComplete = hasCompletedInitialTurn(initialMessages, activeStreamId);
  useEffect(() => {
    if (turnAlreadyComplete) {
      // The rendered transcript already ends with the reply, so a non-null
      // `activeStreamId` here can only be a pre-cleanup leftover. Retire it now
      // rather than leaving it for the first foreground re-attach to pick up.
      activeStreamIdSpentRef.current = true;
      return;
    }
    if (!activeStreamId || hasResumedRef.current) {
      return;
    }
    hasResumedRef.current = true;
    let settled = false;
    let interrupted = false;
    resumeStreamIdempotently().catch(() => {
      // The SDK routes resume failures into the chat's own error status.
    }).finally(() => {
      settled = true;
      if (interrupted) {
        return;
      }
      // The snapshot has now had the one resume it's good for. Retiring it
      // here (not only on a clean finish via `clearRunId`) is what stops a
      // resume that failed — or a run that died without a `finish` chunk —
      // from being re-attached on every later window focus, forever. Runs
      // started in this session keep their session-storage id, so a genuine
      // dropped connection still reconnects.
      activeStreamIdSpentRef.current = true;
    });
    return () => {
      // `useChat`'s own cleanup aborts a still-pending resume, so Strict
      // Mode's mount → cleanup → mount would otherwise leave the remount
      // with no resume at all (blank turn). Re-arm only an interrupted
      // attempt; the SDK supersedes the aborted one without a status flip.
      if (!settled) {
        interrupted = true;
        hasResumedRef.current = false;
      }
    };
  }, [activeStreamId, resumeStreamIdempotently, turnAlreadyComplete]);

  // No further unmount abort here: the `chat.stop()` cleanup above (standing
  // in for what `useChat` does when it owns the `Chat`) runs on unmount and
  // on chat switch, which closes this client's read cursor (the durable run
  // keeps streaming server-side) and frees the chat's reader slot so the
  // next mount's resume attaches. Without that, an orphaned `Chat` held the
  // slot and the remounted view's catch-up resume was silently suppressed —
  // the turn rendered blank until a hard reload.

  // Re-attach a dropped stream when the tab returns to the foreground.
  // Browsers aggressively suspend background tabs — throttled timers, paused
  // `requestAnimationFrame` (which freezes the smooth-text reveal), and often a
  // torn-down SSE connection. The server run keeps going on the durable belt,
  // but the client never reconnects, so the turn looks frozen wherever it was
  // when the user switched away (reported repeatedly; feedback
  // ZEBLPnO9w8kow42dGlAee). The one-shot `hasResumedRef` above only covers the
  // initial mount; this covers every subsequent foreground transition.
  //
  // Safe to fire liberally, but only because `getRunId()` is trustworthy about
  // what is resumable: it returns `null` for a user-stopped run, and it only
  // offers the server's `activeStreamId` snapshot once (see
  // `activeStreamIdSpentRef`) so a finished run can't be re-attached on every
  // foreground. The reader registry additionally suppresses a resume while a
  // live `send` still owns the chat's reader slot. We gate on status to avoid
  // churning a healthy live connection: while `streaming`/`submitted` the store
  // is being fed, so a reconnect would only abort-and-replay it.
  const statusRef = useLatest(status);
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const reattachIfForegrounded = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const current = statusRef.current;
      if (current === "streaming" || current === "submitted") {
        return;
      }
      // Nothing was in flight when this chat was rendered and we haven't sent
      // anything since, so there is no turn of ours to catch up on. Skipping
      // keeps a plain alt-tab on a finished chat from firing a reconnect at all.
      if (turnAlreadyComplete && !sentLocallyRef.current) {
        return;
      }
      resumeStreamIdempotently().catch(() => {
        // Resume failures surface via the chat's own error status.
      });
    };
    document.addEventListener("visibilitychange", reattachIfForegrounded);
    window.addEventListener("focus", reattachIfForegrounded);
    return () => {
      document.removeEventListener("visibilitychange", reattachIfForegrounded);
      window.removeEventListener("focus", reattachIfForegrounded);
    };
  }, [resumeStreamIdempotently, statusRef, turnAlreadyComplete]);

  // Serialize a new send behind any in-flight stop so cancel + clear land
  // first (voice barge-in correctness — see web ADR notes).
  const stopChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const wrappedSendMessage = useCallback<typeof sendMessage>((input, sendOptions_0) => {
    sentLocallyRef.current = true;
    const pendingStop = stopChainRef.current;
    stopChainRef.current = Promise.resolve();
    return pendingStop.catch(() => undefined).then(() => sendMessage(input, sendOptions_0));
  }, [sendMessage]);
  const isActive = status === "streaming" || status === "submitted";
  const smoothTextEnabled = sentLocallyRef.current || !isActive || resumeReplayComplete;

  // Defence-in-depth fallback for pure transport/platform failures where no
  // assistant row was persisted.
  const [emptyResponseError, setEmptyResponseError] = useState<Error | null>(null);
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev_1 = prevStatusRef.current;
    prevStatusRef.current = status;
    if (status === "ready" && (prev_1 === "submitted" || prev_1 === "streaming") && !sdkError) {
      const lastMsg = messages.at(-1);
      if (lastMsg && lastMsg.role === "user" && !userStoppedRef.current) {
        setEmptyResponseError(new Error("Something went wrong generating this response. Please try again."));
      }
    }
    if (status === "ready") {
      userStoppedRef.current = false;
    }
    if (status === "submitted" || status === "streaming") {
      setEmptyResponseError(null);
    }
  }, [status, messages, sdkError]);
  const error_0 = sdkError ?? emptyResponseError ?? undefined;

  // Fire once when a brand-new chat starts streaming.
  const hasFirstStreamedRef = useRef(false);
  useEffect(() => {
    if (!hasFirstStreamedRef.current && initialMessages.length === 0 && status === "streaming") {
      hasFirstStreamedRef.current = true;
      onFirstStream?.();
    }
  }, [status, initialMessages.length, onFirstStream]);

  // Stop with partial-message persistence via the host callback.
  const handleStop = useCallback(() => {
    // Read the store, not React's `messages`: the React copy trails the store
    // by up to one coalesced notification, so the last streamed tokens would
    // otherwise be missing from what the host persists.
    const newMessages = chat.messages.slice(initialMessages.length);
    userStoppedRef.current = true;

    // Mark this run non-resumable and drop its session marker *before*
    // aborting, so any resume the abort triggers (mount/reconnect) sees a
    // stopped run via `getRunId` and short-circuits instead of re-attaching to
    // the still-live belt (the model keeps generating post-cancel — see
    // `stoppedRunIdsRef`). The server clears its own `activeStreamId` in
    // `ChatService.stop`, so a hard refresh is already covered; this handles
    // the same-session case.
    // Either id may be the run being read, since resume prefers the server's.
    const runKey_1 = `workflow-run:${chatId}`;
    for (const stoppedRunId of [typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(runKey_1), activeStreamIdRef.current]) {
      if (stoppedRunId) {
        stoppedRunIdsRef.current.add(stoppedRunId);
      }
    }
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(runKey_1);
    }

    // Tear the active read down through the reader registry, not just the AI
    // SDK's `stop()`. On a *resumed* stream `stop()` is a visible no-op: it
    // aborts the per-request controller, but the SDK never threads that
    // signal into `reconnectToStream` (only `sendMessages` gets an
    // `abortSignal`), so the reconnect fetch kept streaming and the stop
    // button did nothing until the server run wound down. The registry lease
    // holds the controller that IS wired into the read — send and resume
    // alike — so this ends both. `stop()` still runs for its SDK-side
    // bookkeeping (and is what actually aborts a live send's fetch).
    abortActiveReader(chatId);
    Promise.resolve().then(() => stop()).catch((err: unknown) => {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      throw err;
    });
    const stopped = Promise.resolve(onStop?.(newMessages)).catch(() => undefined);
    stopChainRef.current = stopChainRef.current.catch(() => undefined).then(() => stopped);
  }, [initialMessages.length, chat, stop, onStop, chatId, activeStreamIdRef]);
  return {
    messages,
    setMessages,
    sendMessage: wrappedSendMessage,
    stop: handleStop,
    status,
    error: error_0,
    smoothTextEnabled
  };
}
export type MagnusSendMessage = UseMagnusResult["sendMessage"];

// ---------------------------------------------------------------------------
// Deprecated aliases (plan 008 rename: the primary chat hook is `useMagnus`,
// matching `createMagnus` / `MagnusProvider` / `magnus.chats`).
// ---------------------------------------------------------------------------

/** @deprecated Renamed — use `useMagnus`. */
export const useAgent = useMagnus;
/** @deprecated Renamed — use `UseMagnusOptions`. */
export type UseAgentOptions = UseMagnusOptions;
/** @deprecated Renamed — use `UseMagnusResult`. */
export type UseAgentResult = UseMagnusResult;
/** @deprecated Renamed — use `MagnusSendMessage`. */
export type AgentSendMessage = MagnusSendMessage;